// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { NotificationService } from '@way/theme-host/services/notification.service';
import { CommonModule } from '@angular/common';
import { PackageService } from '@way/theme-host/services/package.service';
import { Package } from '@way/theme-host/shared/types';
import {
  PageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { PageFooterComponent } from '../shared/footer/footer.component';
import {
  SharedPackageSelectionModalComponent,
  SelectedPackage,
} from '@way/website-themes/themes/shared/package-selection-modal/package-selection-modal.component';
import { PACKAGE_ICON_FALLBACK } from '@way/theme-host/shared/constants/website-builder-assets';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'express-details'
    | 'packages'
    | 'coupons'
    | 'fleets';
  content: any;
  selected?: boolean;
}

@Component({
  selector: 'app-express-auto-details-preview',
  standalone: true,
  providers: [NotificationService],
  imports: [
    CommonModule,
    PageHeaderComponent,
    PageFooterComponent,
    SharedPackageSelectionModalComponent,
  ],
  templateUrl: './express-auto-details-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./express-auto-details-preview.component.scss'],
})
export class ExpressAutoDetailsPreviewComponent implements OnInit, OnChanges {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {}; // Section visibility control
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  packages: Package[] = [];
  loading = false;
  error: string | null = null;

  // Package selection modal
  showPackageSelectionModal = false;
  selectedPackageForModal: Package | null = null;

  // Page data from JSON
  headerData: any = null;
  pageTitleData: any = null;
  servicesSectionData: any = null;
  footerData: any = null;

  constructor(
    private logger: LoggerService,
    private packageService: PackageService
  ) {}

  ngOnInit(): void {
    this.loadPageContent();
    this.loadPackages();
  }

  /**
   * Handle input changes to update page content, header navigation, and services/packages data when relevant inputs change.
   * @example
   * gOnChanges({ pageData: { firstChange: false }, elements: { firstChange: false }, pages: {}, currentPageType: 'home' })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Angular SimpleChanges object describing which input properties have changed.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color and packages updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of pageTitleData and servicesSectionData to get updated background color and packages
      const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
      const sections = page?.sections || {};
      this.pageTitleData = sections['page-title'] || {};
      this.servicesSectionData = sections.services_section || {};

      // Also check if services element has packages data in its content
      const servicesElement = this.elements.find(
        e =>
          e.id === 'express-details' ||
          e.type === 'express-details' ||
          (e as any).originalKey === 'services_section'
      );
      if (servicesElement?.content?.packages) {
        // Merge packages from element content with servicesSectionData
        if (!this.servicesSectionData.packages) {
          this.servicesSectionData.packages = [];
        }
        // Update packages array with latest buttonText from element content
        servicesElement.content.packages.forEach((pkg: any) => {
          const existingPkg = this.servicesSectionData.packages.find((p: any) => p.id === pkg.id);
          if (existingPkg) {
            existingPkg.buttonText = pkg.buttonText;
            existingPkg.scaletype = pkg.scaletype;
            existingPkg.image = pkg.image;
          } else {
            this.servicesSectionData.packages.push(pkg);
          }
        });
      }
    }
  }

  /**
   * Loads and initializes the component's page section data (header, page title, services, footer) from this.pageData.
   * @example
   * loadPageContent()
   * void
   * @param {{void}} {{none}} - No arguments.
   * @returns {{void}} Initializes internal section state and updates header navigation.
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('✨ Loading Express Auto Details page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || {};

    this.headerData = sections.header || {};
    this.pageTitleData = sections['page-title'] || {};
    this.servicesSectionData = sections.services_section || {};
    this.footerData = sections.footer || {};

    this.logger.debug('Express Auto Details sections loaded:', {
      header: this.headerData,
      hero: this.pageTitleData,
      services: this.servicesSectionData,
      footer: this.footerData,
    });

    // Update header navigation to match current pages
    this.updateHeaderNavigation();
  }

  /**
   * Update the component's headerData.navigation from pages and ensure a default CTA button exists.
   * @example
   * updateHeaderNavigation()
   * undefined
   * @returns {void} Populates this.headerData.navigation by filtering visible pages and mapping them to {label, link, active}, and ensures a default ctaButton when none is present.
   **/
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug(
        'Updated header navigation for Express Auto Details:',
        this.headerData.navigation
      );
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Loads detailing packages from the packageService, updates this.packages, and manages loading/error state.
   * @example
   * this.loadPackages()
   * // void
   * @returns {void} No return value; method updates component state asynchronously.
   **/
  private loadPackages(): void {
    this.loading = true;
    this.error = null;

    this.packageService.getDetailingPackages().subscribe({
      next: response => {
        this.logger.debug('✅ Detailing packages loaded:', response);
        if (response.status && response.data) {
          this.packages = response.data;
          this.logger.debug('Packages count:', this.packages.length);
        } else {
          this.error = 'No detailing packages available';
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('❌ Error loading detailing packages:', err);
        this.error = 'Failed to load detailing packages';
        this.loading = false;
      },
    });
  }

  // Format price for display
  formatPrice(price: number | string | undefined): string {
    if (!price) return '$0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  }

  // Get package image or fallback
  getPackageImage(pkg: Package): string {
    // Use image from API if available, otherwise fall back to static placeholder
    if (pkg.image && pkg.image.trim() !== '') {
      return pkg.image;
    }
    return PACKAGE_ICON_FALLBACK;
  }

  // Get package image fit/scaletype
  /**
   * Determine CSS object-fit value for a package image based on the package's scaletype in servicesSectionData.
   * @example
   * etPackageImageFit({ id: 'pkg1' })
   * 'cover'
   * @param {{Package}} {{pkg}} - Package object to get the scaletype for.
   * @returns {{string}} Return the CSS object-fit value ('fill', 'cover', 'contain', 'scale-down').
   **/
  getPackageImageFit(pkg: Package): string {
    // Check services_section.packages array for this package's scaletype
    const servicesSectionPackages = this.servicesSectionData?.packages || [];
    const packageConfig = servicesSectionPackages.find((p: any) => p.id === pkg.id);

    if (packageConfig?.scaletype) {
      // Map API scaletype to CSS object-fit values
      const scaletype = packageConfig.scaletype;
      if (scaletype === 'fill' || scaletype === 'stretch') {
        return 'fill';
      } else if (scaletype === 'cover') {
        return 'cover';
      } else if (scaletype === 'contain') {
        return 'contain';
      } else if (scaletype === 'fit') {
        return 'scale-down';
      }
    }

    // Default to cover if not specified
    return 'cover';
  }

  // Get package button text from API data
  /**
   * Return the display text for the action button associated with a package using prioritized sources.
   * @example
   * etPackageButtonText({id: 'standard'})
   * 'Book Appointment'
   * @param {{Package}} {{pkg}} - Package object to lookup the button text for.
   * @returns {{string}} The button text to display for the package (falls back to 'Book Appointment').
   **/
  getPackageButtonText(pkg: Package): string {
    // Priority 1: Check services element content for packages array (most up-to-date)
    const servicesElement = this.elements.find(
      e =>
        e.id === 'express-details' ||
        e.type === 'express-details' ||
        (e as any).originalKey === 'services_section'
    );
    if (servicesElement?.content?.packages) {
      const elementPackage = servicesElement.content.packages.find((p: any) => p.id === pkg.id);
      if (elementPackage?.buttonText) {
        return elementPackage.buttonText;
      }
    }

    // Priority 2: Check services_section.packages array from pageData
    const servicesSectionPackages = this.servicesSectionData?.packages || [];
    const packageConfig = servicesSectionPackages.find((p: any) => p.id === pkg.id);

    if (packageConfig?.buttonText) {
      return packageConfig.buttonText;
    }

    // Default to 'Book Appointment' if not specified
    return 'Book Appointment';
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = PACKAGE_ICON_FALLBACK;
  }

  // Get display price
  getListPrice(pkg: Package): string {
    return pkg.single_list_price;
  }

  // Get strikeoff price (discounted price)
  getStrikeoffPrice(pkg: Package): string {
    return pkg.single_strikeoff_price || pkg.single_list_price;
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  /**
   * Returns the header logo information (image URL, text, and link), using header content values when present and sensible defaults otherwise.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Logo', link: '/' }
   * @returns {{image: string, text: string, link: string}} Header logo object containing image, text and link.
   **/
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.logo) {
      return {
        image: headerContent.logo.image || headerContent.logo.url || '',
        text: headerContent.logo.text || 'Logo',
        link: headerContent.logo.link || '/',
      };
    }
    return { text: 'Logo', link: '/' };
  }

  /**
   * Build and return the header navigation items, ensuring each item has a pageType (inferred from link or site pages) and falling back to visible pages when explicit navigation is not provided.
   * @example
   * etHeaderNavigation()
   * [{ label: 'Home', link: '#home', pageType: 'home', active: false }]
   * @param {void} noArgs - No parameters.
   * @returns {NavigationItem[]} An array of navigation items with label, link, pageType and active flag.
   */
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      // Ensure navigation items have pageType set
      return headerContent.navigation.map((nav: any) => {
        // If pageType is not set, try to extract it from link or find matching page
        if (!nav.pageType) {
          const matchingPage = this.pages?.find(
            (p: any) =>
              p.name === nav.label ||
              (p as any).pageType === nav.link?.replace(/^#+|\/+$/g, '').replace(/^\/+/, '')
          );
          if (matchingPage) {
            nav.pageType = (matchingPage as any).pageType;
          } else {
            // Extract from link
            const normalizedLink = (nav.link || '')
              .replace(/^#+|\/+$/g, '')
              .replace(/^\/+/, '')
              .toLowerCase();
            if (
              normalizedLink === 'expresswashes' ||
              normalizedLink === 'express-washes' ||
              normalizedLink.includes('express-wash')
            ) {
              nav.pageType = 'express-wash-packages';
            } else if (
              normalizedLink === 'expressdetails' ||
              normalizedLink === 'express-details' ||
              normalizedLink.includes('express-detail') ||
              normalizedLink.includes('express-auto')
            ) {
              nav.pageType = 'express-auto-details';
            } else if (
              normalizedLink === 'fleets' ||
              normalizedLink === 'fleet' ||
              normalizedLink.includes('fleet')
            ) {
              nav.pageType = 'fleet-management';
            } else if (
              normalizedLink === 'coupons' ||
              normalizedLink === 'coupon' ||
              normalizedLink.includes('coupon')
            ) {
              nav.pageType = 'coupons';
            } else if (
              normalizedLink === '' ||
              normalizedLink === 'home' ||
              normalizedLink === '/'
            ) {
              nav.pageType = 'home';
            } else {
              nav.pageType = normalizedLink || 'home';
            }
          }
        }
        return nav;
      });
    }
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        pageType: (page as any).pageType || 'home', // Ensure pageType is set
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Get the CTA button configuration from the header content, falling back to sensible defaults.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @returns {{CTAButton | null}} CTA button object containing text, link, backgroundColor and textColor, or null.
   **/
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    const primaryColor = this.getPrimaryColor();
    if (headerContent?.ctaButton) {
      return {
        text: headerContent.ctaButton.text || headerContent.ctaButton.label || 'Book Wash',
        link: headerContent.ctaButton.link || '#',
        // Use button's backgroundColor if provided, otherwise fall back to primaryColor
        backgroundColor: headerContent.ctaButton.backgroundColor || primaryColor || '#1B5BFF',
        textColor: headerContent.ctaButton.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#',
      backgroundColor: primaryColor,
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#6B7280';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Manrope';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get hero title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    return 'Express Auto Details – Shine in No Time!';
  }

  /**
   * Get hero subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    return "Whether it's a quick clean or a full headlamp refresh, our Express Detail Packages give your ride that 'like-new' look without the wait.";
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    // Check page-level title first (highest priority)
    // Handle both editor and preview formats
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.title && typeof page.title === 'object' && page.title.style?.text_style) {
      const titleStyleObj = page.title.style.text_style;
      const styles: any = {};
      if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
      if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
      if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
      if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
      return styles;
    }

    // Fallback to section-level title
    if (this.pageTitleData?.title && typeof this.pageTitleData.title === 'object') {
      const title = this.pageTitleData.title;
      const titleStyleObj = title.style?.text_style;

      if (titleStyleObj && typeof titleStyleObj === 'object') {
        const styles: any = {};
        if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
        if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
        if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
        if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
        return styles;
      }
    }

    return {};
  }

  /**
   * Get hero section element for selection
   */
  getPageHeaderSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'express-details' ||
        el.id === 'express-details' ||
        (el as any).originalKey === 'page-title'
    );
  }

  /**
   * Get page header section background color
   */
  getPageHeaderSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const pageHeaderElement = this.getPageHeaderSectionElement();
    const pageHeaderContent = pageHeaderElement?.content as any;
    if (pageHeaderContent?.backgroundColor) {
      return pageHeaderContent.backgroundColor;
    }
    // Priority 2: Check pageTitleData (from API/pageData)
    if (this.pageTitleData?.backgroundColor) {
      return this.pageTitleData.backgroundColor;
    }
    // Priority 3: Check services section background color (also saved there)
    if (this.servicesSectionData?.backgroundColor) {
      return this.servicesSectionData.backgroundColor;
    }
    // Priority 4: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 5: Check pageData directly
    const sections = page?.sections || {};
    const pageTitleSection = sections['page-title'] || {};
    if (pageTitleSection.backgroundColor) {
      return pageTitleSection.backgroundColor;
    }
    // Priority 6: Check services section in pageData
    const servicesSection = sections.services_section || {};
    if (servicesSection.backgroundColor) {
      return servicesSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get services section background color (same as page header section)
   */
  getServicesSectionBackgroundColor(): string | null {
    // Use the same background color as the page header section
    return this.getPageHeaderSectionBackgroundColor();
  }

  /**
   * Handle hero section click
   */
  onPageHeaderSectionClick(event: Event): void {
    // Don't stop propagation here - let header clicks bubble up and be handled separately
    // Only handle if the click is not on the header wrapper
    const target = event.target as HTMLElement;
    if (target.closest('.page-header-header-wrapper')) {
      // Click was on header, let it be handled by onHeaderClick
      return;
    }

    event.stopPropagation();
    const heroElement: WebsiteElement = {
      id: 'express-details',
      type: 'express-details',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        backgroundColor:
          this.getPageHeaderSectionBackgroundColor() || this.pageTitleData?.backgroundColor,
      },
      selected: true,
      originalKey: 'page-title',
    } as any;
    this.logger.debug('✨ Hero section clicked, emitting element:', heroElement);
    this.elementSelect.emit(heroElement);
  }

  /**
   * Return footer quick links. Uses footer.content.quickLinks if present and non-empty; otherwise builds links from this.pages (excluding pages with visible === false). Returns an empty array if no links or pages are available.
   * @example
   * etFooterQuickLinks()
   * [{ label: 'Contact Us', link: '#contact-us' }, { label: 'About', link: '#about' }]
   * @param {{void}} none - This method does not accept any arguments.
   * @returns {{Array<{ label: string; link: string }>} } Return an array of objects each containing a label and a link.
   **/
  getFooterQuickLinks(): { label: string; link: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Returns an array of social link objects parsed from the component's footer content.
   * @example
   * etFooterSocialLinks()
   * [{ platform: "Twitter", url: "https://twitter.com/example", icon: "twitter" }]
   * @returns {{Array<{ platform: string; url: string; icon: string }>}} Returns an array of social link objects with platform, url and icon as strings.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.socialLinksTitle || 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.quickLinksTitle || 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.copyright) {
      return footerContent.copyright;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Get footer logo (text and image) from the template's footer content, falling back to header logo or a default.
   * @example
   * etFooterLogo()
   * { text: 'way', image: 'logo.png' }
   * @param {{void}} none - No arguments.
   * @returns {{ {text?: string; image?: string} | undefined }} Footer logo object with optional text and image, or undefined.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  /**
   * Get services section element for selection
   */
  getServicesSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.id === 'express-details' ||
        el.type === 'express-details' ||
        (el as any).originalKey === 'services_section'
    );
  }

  /**
   * Handle services section click
   */
  onServicesSectionClick(event: Event): void {
    // Stop event propagation to prevent conflicts
    event.stopPropagation();

    const servicesElement: WebsiteElement = {
      id: 'express-details',
      type: 'express-details',
      content: {
        ...(this.servicesSectionData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        pageTitleData: this.pageTitleData,
        backgroundColor: this.getServicesSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'services_section',
    } as any;

    this.logger.debug('✨ Services section clicked, emitting element:', servicesElement);
    this.elementSelect.emit(servicesElement);
  }

  onHeaderClick(event?: Event): void {
    // Stop propagation to prevent triggering page header section click
    if (event) {
      event.stopPropagation();
    }
    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  onBuyNowClick(pkg: Package, event: Event): void {
    event.stopPropagation();
    this.selectedPackageForModal = pkg;
    this.showPackageSelectionModal = true;
  }

  onClosePackageModal(): void {
    this.showPackageSelectionModal = false;
    this.selectedPackageForModal = null;
  }

  onPackageSelected(selected: SelectedPackage): void {
    this.logger.debug('Package selected:', selected);
    // Handle the selected package here
    // You can emit an event or perform any action needed
    // Don't close the modal automatically - let user close it manually or proceed to next step
  }

  getPackagesForModal(): Package[] {
    if (this.selectedPackageForModal) {
      return [this.selectedPackageForModal];
    }
    return this.packages;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
